import { useEffect, useState, useMemo } from "react";
import { Box, Typography, Divider, Button } from "@mui/material";
import useParamValues from "../../utils/useParamValues";
import useTranslationSetup from "../../utils/useTranslationSetup";
import StudentSearchBar from "../../components/StudentSearchBar/StudentSearchBar";
import CohortViewHeader from "../../components/CohortViewHeader/CohortViewHeader";
import StudentListOverlay from "../../components/StudentListOverlay/StudentListOverlay";
import CohortCategoryCard from "../../components/CohortCategoryCard/CohortCategoryCard";
import CohortDetailsContent from "../../components/CohortDetailsContent/CohortDetailsContent";
import CohortBottomDrawer from "../../components/CohortBottomDrawer/CohortBottomDrawer";
import {
  cohortDetailsMap,
  cohortIdMap,
  cohortStaticData,
} from "../../utils/CommonUtils/constants";
import CohortLevelBarSection from "../../components/CohortLevelBar/CohortLevelBarSection";
import downloadImg from "../../assets/download.svg";
import { SCREENS } from "../../utils/CommonUtils/constants";
import "./CohortMainScreen.css";
import { getPaginatedCohortPages } from "../../utils/helper";
import ClassReportCardPdf from "../classReportCardPdf/ClassReportCardPdf";
import { produceEventLog } from "../../utils/helper";
import { getUserProperties } from "../../utils/helper";

const CohortMainScreen = ({ students, setActiveScreen }) => {
  const { classNum, section } = useParamValues();
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslationSetup();
  const [openCategory, setOpenCategory] = useState(null);
  const [selectedCohort, setSelectedCohort] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [cohortDetailsMapState, setCohortDetailsMap] =
    useState(cohortDetailsMap);
  const [showPdfContent, setShowPdfContent] = useState(false);
  const userProperties = getUserProperties(useParamValues());

  const cohortGroups = useMemo(() => {
    const groups = {
      beeg: [],
      ankuran: [],
      pushpan: [],
      falan: [],
    };

    const baseStudents = searchTerm.trim() ? searchResults : students;

    baseStudents.forEach((student) => {
      const cohortKey = cohortIdMap[student.cohort];
      if (cohortKey && groups[cohortKey]) {
        groups[cohortKey].push(student);
      }
    });

    return groups;
  }, [students]);

  useEffect(() => {
    produceEventLog("results_2b_landing_page_view", {
      ...userProperties,
    });
  }, []);

  useEffect(() => {
    const updatedCohortDetailsMap = { ...cohortDetailsMap };

    Object.keys(cohortGroups).forEach((cohortKey) => {
      updatedCohortDetailsMap[cohortKey] = {
        ...updatedCohortDetailsMap[cohortKey],
        students: cohortGroups[cohortKey],
      };
    });

    setCohortDetailsMap(updatedCohortDetailsMap);

    const updatedCategories = Object.keys(cohortGroups).map((key) => {
      const cohortId = Object.keys(cohortIdMap).find(
        (id) => cohortIdMap[id] === key
      );
      return {
        ...cohortStaticData[cohortId],
        id: cohortId,
        count: cohortGroups[key].length,
      };
    });

    setCategories(updatedCategories);
  }, [cohortGroups]);

  const handleLearnMore = (id) => {
    setSelectedCohort(id);
    setIsDrawerOpen(true);
    produceEventLog("result_2b_cohort_know_more_button", {
      ...userProperties,
      cohort_information: id,
    });
  };

  const paginatedPages = getPaginatedCohortPages(students, 20, 25);

  const handleToggle = (id) => {
    const newValue = openCategory === id ? null : id;
    setOpenCategory(newValue);

    if (newValue) {
      produceEventLog("result_2b_cohort_expand_button", {
        ...userProperties,
        cohort_information: id,
      });
    }
  };

  const selectedCat = categories.find((cat) => cat.id === selectedCohort);
  const cohortKey = selectedCat ? cohortIdMap[selectedCat.id] : null;
  const cohortDetails = cohortKey ? cohortDetailsMapState[cohortKey] : null;

  return (
    <Box>
      <Box
        className={`cohort-main-screen-header-wrapper ${searchTerm.trim() ? "dimmed" : ""}`}
      >
        <CohortViewHeader
          onBack={() => {
            sessionStorage.setItem(
              "activeScreen",
              SCREENS.RENDER_INSTRUCTIONS_SCREEN
            );
            setActiveScreen(SCREENS.RENDER_INSTRUCTIONS_SCREEN);
          }}
          showBack
        />
      </Box>
      <Box className="cohort-view-screen-container">
        <Typography className="cohort-view-screen-header">
          {t("class_heading", { classNum, section: section.toUpperCase() })}
        </Typography>
        <Typography className="cohort-view-screen-side-text">
          {t("class_heading_side_text")}
        </Typography>
        <CohortLevelBarSection categories={categories} />
        <Divider className="cohort-view-divider" />
        <StudentSearchBar
          students={students}
          onResults={setSearchResults}
          onSearchTermChange={setSearchTerm}
          searchTerm={searchTerm}
        />

        {searchTerm.trim() && (
          <StudentListOverlay
            results={searchResults}
            searchTerm={searchTerm}
            onCloseList={() => {
              setSearchTerm("");
              setSearchResults([]);
            }}
            cohortIdMap={cohortIdMap}
          />
        )}
        <Typography className="cohorts-header">
          {t("cohort_view_heading")}
        </Typography>
        <Typography className="cohorts-side-header">
          {t("cohort_view_side_text")}
        </Typography>

        {categories.map((cat) => {
          const cohortKey = cohortIdMap[cat.id];
          const cohortDetails = cohortDetailsMapState[cohortKey];

          return (
            <CohortCategoryCard
              key={cat.id}
              title={cat.title}
              className={cat.className}
              count={cat.count}
              icon={cat.icon}
              isOpen={openCategory === cat.id}
              onToggle={() => handleToggle(cat.id)}
              cohortDetails={cohortDetails}
              classNum={classNum}
            >
              <CohortDetailsContent
                {...cohortDetails}
                onLearnMore={() => handleLearnMore(cat.id)}
                className={cat.className}
              />
            </CohortCategoryCard>
          );
        })}

        {selectedCohort && selectedCat && cohortDetails && (
          <CohortBottomDrawer
            open={isDrawerOpen}
            cohort={selectedCat}
            onClose={() => setIsDrawerOpen(false)}
            image={selectedCat.icon}
            title={cohortDetails.title}
            gifTextArray={selectedCat.gifTextArray}
            gifText={selectedCat.gifText}
            cohortDetails={cohortDetails}
            classNum={classNum}
          />
        )}
      </Box>
      <Box className="cohort-screen-sticky-box">
        <Button
          className={`cohort-view-sticky-button ${searchTerm.trim() || students.length === 0 ? "disabled" : ""}`}
          variant="contained"
          startIcon={
            <img
              src={downloadImg}
              alt="Download Icon"
              className="cohort-view-sticky-button-icon"
            />
          }
          disabled={students.length === 0}
          onClick={() => {
            setShowPdfContent(true);
            produceEventLog("result_2b_class_result_pdf_download_btn", {
              ...userProperties,
            });
          }}
        >
          {t("download_pdf")}
        </Button>
      </Box>
      {showPdfContent && (
        <ClassReportCardPdf
          showPdfContent={showPdfContent}
          setShowPdfContent={setShowPdfContent}
          students={students}
          paginatedPages={paginatedPages}
          categories={categories}
        />
      )}
    </Box>
  );
};

export default CohortMainScreen;
